import { useEffect, useMemo } from 'react'

import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { BannerAddFormContent } from './BannerAddFormContent'

export const Banner = ({
  handleSectionValidity,
  bannersToTable,
  type,
  isSegmentationReady,
  notReadyMessage,
  initialCollapsibleState,
  ...rest
}: BannerPanelProps) => {
  const { t } = useAPPTranslation()

  // Falls back to the original rule — a targeting type was picked in
  // CampaignTargeting — when the caller doesn't override it. Onsite
  // Banner/Video never override this, so their behavior is unchanged.
  const segmentationIsReady =
    isSegmentationReady ?? !!rest?.selectedSegmentation

  /**
   * Handle section is valid
   * * Refactor
   */

  const sectionIsValid = useMemo(
    () => !!bannersToTable?.length,
    [bannersToTable]
  )

  useEffect(
    () => handleSectionValidity?.(sectionIsValid),
    [handleSectionValidity, sectionIsValid]
  )

  const panelTitle =
    type === 'banner_video'
      ? t('rm:campaign.create.BannerVideo.panelTitle')
      : t('rm:campaign.create.Banner.panelTitle')

  return (
    <Panel
      iconLeft={<BadgeIsValid isValid={sectionIsValid} />}
      {...(!sectionIsValid && { className: 'border-danger' })}
      title={panelTitle}
      // The card used to carry the title alone, with a second title and the
      // real subtitle repeated inside the body — the subtitle belongs up
      // here, next to the title, like every other panel in this form
      // (2026-09-15 demo, SPEC 004 item 4.1).
      description={t('rm:campaign.create.Banner.helperText')}
      isCollapsible
      initialCollapsibleState={initialCollapsibleState}
    >
      {!segmentationIsReady && (
        <Alert variant="warning">
          {notReadyMessage ||
            t('rm:campaign.create.alerts.leastOneSegmentationConfiguration')}
        </Alert>
      )}

      {segmentationIsReady && (
        <BannerAddFormContent
          bannersToTable={bannersToTable}
          type={type}
          {...(rest as BannerProps<FormHandles>)}
        />
      )}
    </Panel>
  )
}
